import {
  Component,
  OnInit,
  PLATFORM_ID,
  inject,
  OnDestroy,
  signal,
  ChangeDetectionStrategy,
  effect,
  untracked,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NavigationEnd, RouterOutlet } from '@angular/router';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  HeaderComponent,
  SetEnvironment,
  TokenModel,
  UserService,
  UserState,
  BannerModule,
  BannerV3Service,
  UserModel,
} from '@gea/digital-ui-lib';
import { combineLatest, distinctUntilChanged, filter, first, Observable, tap } from 'rxjs';
import { JwtPayload, jwtDecode } from 'jwt-decode';
import { isEqual } from 'lodash';

import { environment } from '@environments/environment';
import { SpinnerComponent } from './shared/components';
import { MachinesStore, MembershipsStore, OrganizationsStore } from './shared/state';
import { SidebarComponent, BRCHeaderComponent } from '.';
import { BRCBaseComponent } from '@brc/hrt-base-component';
import { extractOrganizationId, isOrganizationRestricted } from './shared/helpers';
import { CustomerOverviewDialogService, BrcDialogService, UserPermissionsService } from './shared/services';
import { ADMIN_ROLES, Organization, OrganizationPermission, Permission } from './shared/models';
import { ROUTE_NAMES } from './route-names';
import packageJson from '../../package.json';

interface CustomJwtPayload extends JwtPayload {
  extension_user_context?: string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'gea-hrt-root',
  standalone: true,
  styleUrls: ['./app.component.scss'],
  imports: [HeaderComponent, SpinnerComponent, RouterOutlet, SidebarComponent, BRCHeaderComponent, BannerModule],
  template: `
    <div class="root" [class.firefox]="firefox()">
      <geaui-header headerTitle="InsightPartner Blu-Red Care" [googleAnalyticsActivated]="true" />

      @if (isLoading()) {
        <div class="spinner">
          <gea-hrt-spinner [diameter]="100" />
        </div>
      } @else {
        @if (contentReady()) {
          <div class="main">
            <gea-hrt-sidebar [currentLanguage]="currentLanguage()" [navigationItems]="navigationItems()" />
            <div class="content">
              <gea-hrt-brc-header />
              <router-outlet />
            </div>
          </div>
        }
      }
    </div>
  `,
})
export class AppComponent extends BRCBaseComponent implements OnInit, OnDestroy {
  readonly contentReady = signal(false);
  readonly firefox = signal(false);

  private readonly _platformId = inject(PLATFORM_ID);
  private readonly userService = inject(UserService);
  private readonly bannerService = inject(BannerV3Service);
  private readonly brcDialogService = inject(BrcDialogService);
  private readonly appVersion = packageJson.version;

  private readonly organizationsStore = inject(OrganizationsStore);
  protected readonly organizations$ = toObservable(this.organizationsStore.organizations).pipe(
    filter(Boolean),
    takeUntilDestroyed(this.destroyRef)
  );
  protected readonly user$ = this.store.select(UserState.user).pipe(
    filter(Boolean),
    takeUntilDestroyed(this.destroyRef),
    tap((user) => this.user.set(user))
  );

  protected readonly isLoading = this.organizationsStore.isLoading;
  protected readonly customerOverviewDialogService = inject(CustomerOverviewDialogService);
  private readonly machinesStore = inject(MachinesStore);
  private readonly membershipsStore = inject(MembershipsStore);
  protected readonly selectedOrg$ = toObservable(this.organizationsStore.selected);
  private readonly userPermissions = inject(UserPermissionsService);
  private readonly user = signal<UserModel | undefined>(undefined);
  private readonly selectedOrg = signal<OrganizationPermission | undefined>(undefined);

  readonly currentLanguage = signal<string>('en-US');

  readonly navigationItems = this.userPermissions.tabs;

  constructor() {
    super();

    effect(() => {
      const isOmniDeprecated = this.machinesStore.isOmniDeprecated();

      if (isOmniDeprecated == undefined) {
        return;
      }

      untracked(() => this.applyOmniDeprecationBanner(isOmniDeprecated));
    });
  }

  ngOnInit(): void {
    if (environment.name) {
      this.store.dispatch(new SetEnvironment(environment.name));
    }

    this.firefox.set(isPlatformBrowser(this._platformId) && navigator.userAgent.toLowerCase().includes('firefox'));
    this.subscribeToUser();
    this.subscribeToSelectedOrganization();

    const token$: Observable<TokenModel> = this.store
      .select(UserState.tokenObject)
      .pipe(filter(Boolean), distinctUntilChanged(isEqual));
    const navigationEnd$ = this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      first()
    );

    combineLatest([token$, navigationEnd$, this.organizations$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([token, navigationEnd, organizations]) => {
        this.resetQueryParams();
        const organizationId = extractOrganizationId(navigationEnd.url);
        const orgFromBeforeRefresh = organizations.find(({ id }) => id === organizationId);

        this.showNewFeaturesNotice(organizations.length);

        if (orgFromBeforeRefresh) {
          this.organizationsStore.setSelectedOrganization(orgFromBeforeRefresh);
          return;
        }

        if (token?.extension_user_context === 'TEMP_MEMBERSHIP') {
          this.handleTempMembership(token, organizations);
        } else {
          this.handlePortalUser(organizations);
        }
      });
  }

  ngOnDestroy(): void {
    this.customerOverviewDialogService.closeDialog();
  }

  private subscribeToUser() {
    combineLatest([this.user$.pipe(filter((u) => !!u.language)), this.store.select(UserState.token)])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([user, token]) => {
        if (user?.language) {
          this.currentLanguage.set(user.language);
          this.translate.use(user.language);
        }

        this.contentReady.set(!!token);

        if (this.contentReady()) {
          try {
            const decodedToken: CustomJwtPayload = jwtDecode(token);
            if (decodedToken.extension_user_context && isPlatformBrowser(this._platformId)) {
              window.userType = decodedToken.extension_user_context;
              window.userId = user.userId;
            }
          } catch {
            this.showError('FAILED_TO_DECODE_TOKEN');
          }
          this.membershipsStore.fetchMemberships();
        }
      });
  }

  private subscribeToSelectedOrganization() {
    this.selectedOrg$
      .pipe(filter(Boolean), distinctUntilChanged(isEqual), takeUntilDestroyed(this.destroyRef))
      .subscribe((selectedOrg) => {
        this.selectedOrg.set(selectedOrg);
        if (!isPlatformBrowser(this._platformId) || !selectedOrg?.organization) {
          return;
        }
        window.organizationName = selectedOrg.organization.name;
      });
  }

  private handlePortalUser(organizations: Organization[]) {
    if (organizations.length >= 1) {
      const org = organizations[0];
      this.organizationsStore.setSelectedOrganization(org);

      if (organizations.length === 1) {
        this.navigateToDefaultRoute(org);
      }
    }
  }

  private handleTempMembership(token: TokenModel, organizations: Organization[]) {
    this.userService.getTempUserMembership(token.oid).subscribe(({ pageEntries }) => {
      const membership = pageEntries[0];
      const organization = organizations.find(({ name }) => name === membership.organizationName);
      const permission = membership.roleName as Permission;

      if (organization) {
        this.organizationsStore.setSelectedOrganization(organization, permission);
        this.navigateToDefaultRoute(organization, permission);
      }
    });
  }

  private navigateToDefaultRoute(organization: Organization, permission?: Permission) {
    const memberships = this.membershipsStore.memberships();
    const resolvedPermission =
      permission ?? (memberships?.find?.((m) => m.organization.name === organization.name)?.role.name as Permission);
    const isAdmin = resolvedPermission ? ADMIN_ROLES.includes(resolvedPermission) : false;

    const isRestricted = !isAdmin && isOrganizationRestricted(organization);

    const route = isRestricted
      ? `${ROUTE_NAMES.OVERVIEW}/${organization.id}`
      : `${ROUTE_NAMES.FAULT_ANALYSIS}/${organization.id}`;

    this.router.navigate([route]);
  }

  private applyOmniDeprecationBanner(isOmniDeprecated: boolean): void {
    const OMNI_DEPRECATION_KEY = 'omni-deprecation';

    this.bannerService.clear(OMNI_DEPRECATION_KEY);
    if (isOmniDeprecated) {
      const omniVersion = this.machinesStore.omniMinRequiredVersion();
      this.bannerService.add({
        key: OMNI_DEPRECATION_KEY,
        severity: 'error',
        closable: true,
        detail: this.translate.instant('BRC_OMNI_DEPRECATION_BANNER_MESSAGE', { omniVersion }),
      });
    }
  }

  private async showNewFeaturesNotice(countOrganizations: number) {
    const { NewFeatureNoticeComponent } = await import('./new-feature-notice/new-feature-notice.component');
    const storedAppVersion = localStorage.getItem('storedAppVersion') || '';
    const isNewVersion = this.isNewVersion(this.appVersion, storedAppVersion);

    if (!isNewVersion) {
      if (countOrganizations > 1) {
        this.customerOverviewDialogService.openDialog();
      }
      // uncomment the line below to show maintenance notice on every login until the notice is dismissed by the user
      // this.showMaintenanceNotice();
      return;
    }

    this.brcDialogService.openDialog(NewFeatureNoticeComponent, {
      header: this.translate.instant('NEW_FEATURES_NOTICE.IMPORTANT_INFORMATION'),
      style: { width: '50vw' },
      onClose: () => {
        localStorage.setItem('storedAppVersion', this.appVersion);
        if (countOrganizations > 1) {
          this.customerOverviewDialogService.openDialog();
        }
        // uncomment the line below to show maintenance notice on every login until the notice is dismissed by the user
        // this.showMaintenanceNotice();
      },
    });
  }

  // private async showMaintenanceNotice() {
  //   const { MaintenanceModalComponent, MAINTENANCE_NOTICE, MAINTENANCE_NOTICE_KEY } =
  //     await import('./maintenance-modal/maintenance-modal.component');
  //   const maintenanceNotice = MAINTENANCE_NOTICE;
  //   const showMaintenanceNotice = sessionStorage.getItem(MAINTENANCE_NOTICE_KEY) !== maintenanceNotice;

  //   if (!showMaintenanceNotice) {
  //     return;
  //   }

  //   this.brcDialogService.openDialog(MaintenanceModalComponent, {
  //     header: this.translate.instant('BRC.MAINTENANCE_MODAL.HEADER'),
  //     style: { width: '50vw' },
  //     onClose: () => {
  //       sessionStorage.setItem(MAINTENANCE_NOTICE_KEY, maintenanceNotice);
  //     },
  //   });
  // }

  /**
   * returns true if appVersion is greater than storedAppVersion
   * or storedAppVersion is not a valid semver
   * @param appVersion
   * @param storedAppVersion
   * @returns
   */
  private isNewVersion(appVersion: string, storedAppVersion: string): boolean {
    return appVersion !== storedAppVersion;
  }
}
